import React, { useContext, useMemo } from 'react';
import { Button, Dropdown, MenuProps, Modal, Select, message } from 'antd';
import * as Icons from '@ant-design/icons';
import { Column } from 'react-table';
import { nextTableColumns } from '@shared/modules/next-table/helpers/next-table-columns';
import { StopPropagation } from '@shared/components/stop-propagation';
import { useBackgroundNavigate } from '@shared/hooks';
import { useBranches } from '@modules/branches';
import { useStatusesByModelId } from '@modules/statuses';
import { useAuth } from '@modules/me';
import { IBranchInspection } from '../../interfaces';
import { branchInspectionQueryKeys } from '../../utils';
import { BranchInspectionsTableContext } from '../../context';
import { BranchInspectionsService } from '../../services';

export const useBranchInspectionsTableColumns = (): Column<IBranchInspection>[] => {
  const navigate = useBackgroundNavigate();
  const { handleFetch } = useContext(BranchInspectionsTableContext);
  const { can } = useAuth();
  const { data: branches = [] } = useBranches(false);
  const { data: statuses = [] } = useStatusesByModelId(51);

  const actionsColumn = useMemo<Column<IBranchInspection>>(
    () => ({
      ...nextTableColumns.actions,
      Cell: ({ row: { original } }: any) => {
        const canScan = original.stateId === 155 || original.stateId === 156;
        const canCancel = original.stateId !== 157;
        const canViewReport = original.stateId === 157;

        const items: MenuProps['items'] = [
          ...(can('branch_inspection_scan')
            ? [
                {
                  key: 'scan',
                  label: 'Yoxlanışa başla',
                  icon: <Icons.ScanOutlined />,
                  disabled: !canScan,
                  onClick: () => navigate(`/branch-inspections/${original.id}/scan`),
                } as NonNullable<MenuProps['items']>[number],
              ]
            : []),
          ...(can('branch_inspection_report')
            ? [
                {
                  key: 'details',
                  label: 'Ətraflı',
                  icon: <Icons.EyeOutlined />,
                  onClick: () => navigate(`/branch-inspections/${original.id}/details`, { withBackground: true }),
                } as NonNullable<MenuProps['items']>[number],
              ]
            : []),
          ...(can('branch_inspection_update')
            ? [
                {
                  key: 'cancel',
                  label: 'Ləğv et',
                  icon: <Icons.CloseCircleOutlined />,
                  danger: true,
                  disabled: !canCancel,
                  onClick: () =>
                    Modal.confirm({
                      title: 'Yoxlanışı ləğv et',
                      content: `#${original.id} nömrəli yoxlanışı ləğv etməyə əminsiniz?`,
                      okText: 'Bəli',
                      cancelText: 'Xeyr',
                      okButtonProps: { danger: true },
                      onOk: async () => {
                        const result = await BranchInspectionsService.changeStatus(original.id.toString(), 158);
                        if (result.status === 200) {
                          message.success(`#${original.id} nömrəli yoxlanış ləğv edildi`);
                          handleFetch();
                        } else message.error(result.data as string);
                      },
                    }),
                } as NonNullable<MenuProps['items']>[number],
              ]
            : []),
          ...(can('branch_inspection_report')
            ? [
                {
                  key: 'report',
                  label: 'Hesabat',
                  icon: <Icons.FileTextOutlined />,
                  disabled: !canViewReport,
                  onClick: () => navigate(`/branch-inspections/${original.id}/report`, { withBackground: true }),
                } as NonNullable<MenuProps['items']>[number],
              ]
            : []),
        ];

        return (
          <StopPropagation>
            <Dropdown menu={{ items }} trigger={['hover']}>
              <Button icon={<Icons.MoreOutlined />} size="small" />
            </Dropdown>
          </StopPropagation>
        );
      },
    }),
    [navigate, handleFetch, can],
  );

  const baseColumns = useMemo<Column<IBranchInspection>[]>(
    () => [
      { ...nextTableColumns.small, accessor: (r) => r.id, id: branchInspectionQueryKeys.id, Header: 'Kod' },
      {
        ...nextTableColumns.large,
        accessor: (r) => r.branch?.name,
        id: branchInspectionQueryKeys.branchId,
        Header: 'Filial',
        Filter: ({ column: { filterValue, setFilter } }: any) => (
          <Select allowClear showSearch optionFilterProp="children" style={{ width: '100%' }} value={filterValue} onChange={setFilter}>
            {branches.map((b) => (
              <Select.Option key={b.id} value={b.id.toString()}>
                {b.name}
              </Select.Option>
            ))}
          </Select>
        ),
      },
      {
        ...nextTableColumns.normal,
        accessor: (r) => r.state?.name || '-',
        id: branchInspectionQueryKeys.stateId,
        Header: 'Status',
        Filter: ({ column: { filterValue, setFilter } }: any) => (
          <Select allowClear style={{ width: '100%' }} value={filterValue} onChange={setFilter}>
            {statuses.map((s) => (
              <Select.Option key={s.id} value={s.id.toString()}>
                {s.name}
              </Select.Option>
            ))}
          </Select>
        ),
      },
      { ...nextTableColumns.date, accessor: (r) => r.deadline.value, id: branchInspectionQueryKeys.deadline, Header: 'Son tarix' },
      { ...nextTableColumns.date, accessor: (r) => r.startedAt, id: branchInspectionQueryKeys.startedAt, Header: 'Başlanma tarixi' },
      { ...nextTableColumns.date, accessor: (r) => r.completedAt, id: branchInspectionQueryKeys.completedAt, Header: 'Tamamlanma tarixi' },
      { ...nextTableColumns.normal, accessor: (r) => r.createdByUser?.name || '-', id: branchInspectionQueryKeys.createdByUserName, Header: 'Yaradan' },
      { ...nextTableColumns.normal, accessor: (r) => r.executedByUser?.name || '-', id: branchInspectionQueryKeys.executedByUserName, Header: 'İcraçı' },
      { ...nextTableColumns.date, accessor: (r) => r.createdAt, id: branchInspectionQueryKeys.createdAt, Header: 'Yaradılma tarixi' },
    ],
    [branches, statuses],
  );

  return useMemo(() => [actionsColumn, ...baseColumns], [actionsColumn, baseColumns]);
};
